import React, { useState } from 'react';
import { Card, Table, DatePicker, Button, Space, ConfigProvider, theme } from 'antd';
import { FileExcelOutlined, PrinterOutlined, ReconciliationOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useAuthStore from '../../store/useAuthStore';
import { getPurchaseRegister } from '../../api/reports.api';
import { formatCurrency, formatDate, exportToCSV } from '../../utils/formatters';

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-purchase-register-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-purchase-register-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 10px 14px;
      margin-bottom: 16px;
    }

    .light-purchase-register-page .ant-picker-sm,
    .light-purchase-register-page .ant-btn-sm {
      border-radius: 4px;
      font-size: 12px;
    }

    /* Table Header - Not pure white */
    .light-purchase-register-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      padding: 6px 10px !important;
      border-bottom: 1px solid var(--border-hi) !important;
    }

    .light-purchase-register-page .ant-table-tbody > tr > td {
      padding: 6px 10px !important;
      font-size: 13px;
      color: var(--text-1);
      border-bottom: 1px solid var(--border);
    }

    /* Print styling to hide controls */
    @media print {
      .no-print { display: none !important; }
      .light-purchase-register-page { padding: 0 !important; }
      .light-purchase-register-page .ant-card { border: none !important; box-shadow: none !important; }
    }
  `}</style>
);

const S = {
  tableWrap: { border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }
};

const PurchaseRegisterPage = () => {
  const { user } = useAuthStore();
  const [dates, setDates] = useState([dayjs().startOf('month'), dayjs()]);

  const { data, isLoading } = useQuery({
    queryKey: ['purchaseRegister', dates],
    queryFn: async () => {
      const res = await getPurchaseRegister(user.plantID, { 
        from: dates[0].format('YYYY-MM-DD'), 
        to: dates[1].format('YYYY-MM-DD') 
      });
      if (res && Array.isArray(res.items)) return res.items;
      if (Array.isArray(res)) return res;
      return [];
    }
  });

  const columns = [
    { title: 'Bill No', dataIndex: 'billNo', key: 'billNo', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { title: 'Date', dataIndex: 'billDate', key: 'billDate', render: v => formatDate(v) },
    { title: 'Vendor', dataIndex: 'vendorName', key: 'vendorName' },
    { title: 'Taxable', dataIndex: 'taxable', key: 'taxable', render: v => formatCurrency(v) },
    { title: 'GST', dataIndex: 'gstAmt', key: 'gstAmt', render: v => <span style={{ color: 'var(--text-3)' }}>{formatCurrency(v)}</span> },
    { title: 'Total', dataIndex: 'totalAmount', key: 'totalAmount', render: v => <span style={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(v)}</span> }
  ];

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-purchase-register-page" style={{ padding: '8px 16px' }}>
        
        <Card
          className="report-card"
          styles={{ 
            header: { padding: '10px 16px', minHeight: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }, 
            body: { padding: '16px' } 
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ReconciliationOutlined style={{ color: 'var(--text-3)' }} />
              <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-1)' }}>
                Purchase Register
              </span>
            </div>
          }
          extra={
            <Space className="no-print">
              <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
              <Button size="small" type="primary" style={{ background: '#10b981' }} icon={<FileExcelOutlined />} onClick={() => exportToCSV(data || [], columns, `PurchaseRegister_${dates[0].format('MMM-YYYY')}`)}>
                Export CSV
              </Button>
            </Space>
          }
        >
          {/* CONTROLS SECTION */}
          <div className="compact-section no-print" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>DATE RANGE :</span>
            <DatePicker.RangePicker 
              size="small" 
              value={dates} 
              onChange={setDates} 
              format="DD MMM YYYY" 
              allowClear={false} 
            />
          </div>

          {/* DATA TABLE */}
          <div style={S.tableWrap}>
            <Table 
              dataSource={data || []} 
              columns={columns} 
              loading={isLoading} 
              rowKey="billNo"
              size="small"
              pagination={false}
              locale={{ emptyText: "No purchases found for the selected dates." }}
              scroll={{ x: 600 }}
              summary={(pageData) => {
                let totalTaxable = 0, totalGst = 0, totalAmount = 0;
                pageData.forEach(({ taxable, gstAmt, totalAmount: total }) => {
                  totalTaxable += taxable || 0; 
                  totalGst += gstAmt || 0; 
                  totalAmount += total || 0;
                });
                return (
                  <Table.Summary.Row style={{ background: 'var(--surface-2)', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px' }}>
                    <Table.Summary.Cell index={0} colSpan={3} style={{ textAlign: 'right', color: 'var(--text-2)' }}>
                      Grand Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{formatCurrency(totalTaxable)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{formatCurrency(totalGst)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <span style={{ color: '#10b981', fontSize: '13px' }}>{formatCurrency(totalAmount)}</span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </div>

        </Card>

      </div>
    </ConfigProvider>
  );
};

export default PurchaseRegisterPage;